import { Check, Layers, MessageSquare, BarChart3 } from 'lucide-react';
import homepageImg from '../../assets/01-Homepage.png';
import burgerMenuImg from '../../assets/02-Burger Menu.png';
import dashboardImg from '../../assets/04-Dashboard.png';
import templatesImg from '../../assets/05-Templates & Forms.png';
import collaborationImg from '../../assets/07-Collaboration.png';
import whiteboardImg from '../../assets/08-Whiteboard.png';
import './Features.css';

function FeatureBullets({ items = [] }) {
  return (
    <ul className="features__bullets">
      {items.map((t) => (
        <li key={t} className="features__bullet">
          <Check className="features__check" aria-hidden="true" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function FeatureImage({ primaryImg, secondaryImg, alt }) {
  return (
    <div className="features__imageWrapper">
      <img
        className="features__image features__image--primary"
        src={primaryImg}
        alt={alt}
        loading="lazy"
      />
      <img
        className="features__image features__image--secondary"
        src={secondaryImg}
        alt={`${alt} - alternate view`}
        loading="lazy"
      />
    </div>
  );
}

export default function Features() {
  return (
    <section id="features" className="features">
      <div className="features__container">
        <div className="features__heading">
          <h2 className="features__kicker">Capabilities</h2>
          <h3 className="features__title">Designed for the future of building.</h3>
          <p className="features__lead">
            Revitron understands architectural and design context. It doesn't just draw lines; it
            understands your workflow in Revit and Rhino, from BIM modeling to 3D design.
          </p>
        </div>

        {/* Feature 1: AI Assistant Interface */}
        <div className="features__row">
          <div className="features__text">
            <div className="features__iconTile features__iconTile--blue">
              <MessageSquare className="features__icon" aria-hidden="true" />
            </div>
            <h4 className="features__rowTitle">AI Assistant for Revit & Rhino</h4>
            <p className="features__rowBody">
              Experience liftoff with our next-generation AI assistant. Generate Python code for
              both Revit and Rhino, automate modeling tasks, and manage your code vault with natural language.
            </p>
            <FeatureBullets
              items={[
                'Natural language to code generation',
                'Supports Revit (IronPython) & Rhino (Python)',
                'Code vault management',
              ]}
            />
          </div>

          <div className="features__media">
            <div className="features__glow features__glow--blue" aria-hidden="true" />
            <FeatureImage
              primaryImg={homepageImg}
              secondaryImg={burgerMenuImg}
              alt="AI Assistant Interface"
            />
          </div>
        </div>

        {/* Feature 2: Dashboard & Templates (reverse) */}
        <div className="features__row features__row--reverse">
          <div className="features__text">
            <div className="features__iconTile features__iconTile--purple">
              <BarChart3 className="features__icon" aria-hidden="true" />
            </div>
            <h4 className="features__rowTitle">Dashboard & Templates</h4>
            <p className="features__rowBody">
              Monitor your model health, track project statistics, and access a library of
              pre-built templates and forms to accelerate your Revit or Rhino workflow.
            </p>
            <FeatureBullets
              items={[
                'Real-time model health monitoring',
                'Comprehensive project analytics',
                'Ready-to-use templates library',
              ]}
            />
          </div>

          <div className="features__media">
            <div className="features__glow features__glow--purple" aria-hidden="true" />
            <FeatureImage
              primaryImg={dashboardImg}
              secondaryImg={templatesImg}
              alt="Dashboard and Templates"
            />
          </div>
        </div>

        {/* Feature 3: Collaboration & Whiteboard */}
        <div className="features__row features__row--last">
          <div className="features__text">
            <div className="features__iconTile features__iconTile--blue">
              <Layers className="features__icon" aria-hidden="true" />
            </div>
            <h4 className="features__rowTitle">Team Collaboration</h4>
            <p className="features__rowBody">
              Work seamlessly with your team. Manage members, assign tasks, and collaborate on
              interactive whiteboards to bring your architectural vision to life together.
            </p>
            <FeatureBullets
              items={[
                'Team management and task assignment',
                'Interactive whiteboard collaboration',
                'Shared workspaces and resources',
              ]}
            />
          </div>

          <div className="features__media">
            <div className="features__glow features__glow--blue" aria-hidden="true" />
            <FeatureImage
              primaryImg={collaborationImg}
              secondaryImg={whiteboardImg}
              alt="Collaboration and Whiteboard"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

